import React, {
  FormEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Helmet } from "react-helmet-async";
import Select from "react-select";
import logo from "../../assets/T4H-favicon.png";
import heroLogo from "../../assets/T4H_TechforHumans_logo.svg";
import { SignInProps } from "../../utils/functionsInterfaces";
import { displayError, displayWarning } from "../../utils/messageToast";
import { useAuthContext } from "../../hooks/auth";
import { SetupNotAuth } from "../../client";
import { clearAppShellCaches } from "../../utils/clearAppCaches";
import { ClientAmbient, ClientOption } from "../../utils/functionsInterfaces";

/** Ambiente padrão do login quando nenhum ambiente da lista (Force) foi escolhido. */
const LOGIN_AMBIENT: ClientAmbient = "prod";

const Login: React.FC = () => {
  const [selectedOption, setSelectedOption] = useState<ClientOption | null>(
    null,
  );
  const { signIn } = useAuthContext();
  /** Filtra o select de clientes e define hml/prod no body do login quando escolhido. */
  const [clientListAmbient, setClientListAmbient] = useState<
    ClientAmbient | ""
  >("");
  const [slug, setSlug] = useState("");
  const [loading, setLoading] = useState(false);
  const [inForce, setInForce] = useState(false);
  const [forceMode, setForceMode] = useState<"rc" | "force" | "">("");
  const [forceEmail, setForceEmail] = useState("");
  const [forcePassword, setForcePassword] = useState("");
  const [clientOptions, setClientOptions] = useState<ClientOption[]>([]);
  const [clientsLoading, setClientsLoading] = useState(false);
  const [googleToken, setGoogleToken] = useState<string | null>(null);
  const [showGoogleButton, setShowGoogleButton] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const showGooglePromptRef = useRef<(() => void) | null>(null);
  const loginValuesRef = useRef({
    slug: "",
    inForce: false,
    forceEmail: "",
    forcePassword: "",
    /** Ambiente efetivo do login: lista (hml/prod) ou padrão quando fora do Force. */
    ambient: LOGIN_AMBIENT as ClientAmbient,
  });

  useEffect(() => {
    const ambientFromList =
      clientListAmbient === "hml" || clientListAmbient === "prod"
        ? clientListAmbient
        : LOGIN_AMBIENT;
    loginValuesRef.current = {
      slug: selectedOption?.value || slug || "",
      inForce,
      forceEmail,
      forcePassword,
      ambient: ambientFromList,
    };
  }, [
    selectedOption,
    slug,
    inForce,
    forceEmail,
    forcePassword,
    clientListAmbient,
  ]);

  useEffect(() => {
    void clearAppShellCaches();
    localStorage.removeItem("googleToken");
  }, []);

  useEffect(() => {
    if (!forceMode || !clientListAmbient) {
      setClientOptions([]);
      return;
    }

    const loadClients = async () => {
      try {
        setClientsLoading(true);
        const api = SetupNotAuth();
        const endpoint =
          forceMode === "force" ? "/clients-force" : "/clients-rc";
        const { data } = await api.get(endpoint);
        const options = (data?.clients || []).map(
          (client: {
            slug: string;
            clientName?: string;
            ambient?: string | null;
          }) => {
            let amb: ClientAmbient | undefined;
            if (client.ambient === "hml") amb = "hml";
            else if (client.ambient === "prod") amb = "prod";
            return {
              value: client.slug,
              label: client.clientName || client.slug,
              ambient: amb,
            };
          },
        );
        setClientOptions(options);
      } catch (_error) {
        displayWarning(
          "Não foi possível carregar os clientes. Tente novamente.",
        );
      } finally {
        setClientsLoading(false);
      }
    };

    loadClients();
  }, [forceMode, clientListAmbient]);

  const filteredClientOptions = useMemo(
    () =>
      clientListAmbient
        ? clientOptions.filter(
          (o) => o.ambient == null || o.ambient === clientListAmbient,
        )
        : [],
    [clientOptions, clientListAmbient],
  );

  useEffect(() => {
    if (!selectedOption?.ambient || !clientListAmbient) return;
    if (selectedOption.ambient !== clientListAmbient) {
      setSelectedOption(null);
      setSlug("");
      localStorage.removeItem("slug");
    }
  }, [clientListAmbient, selectedOption]);

  useEffect(() => {
    setInForce(false);
    setForceMode("");
    setSelectedOption(null);
    setSlug("");
    localStorage.setItem("inForce", "false");
    localStorage.removeItem("slug");
  }, []);

  useEffect(() => {
    if (!inForce) {
      setForceEmail("");
      setForcePassword("");
    }
  }, [inForce]);

  const performLoginWithGoogle = useCallback(
    async (token: string) => {
      const ref = loginValuesRef.current;
      const currentSlug = ref.slug || localStorage.getItem("slug") || "";
      if (!currentSlug?.trim()) {
        displayWarning("Selecione o cliente antes de continuar.");
        setGoogleLoading(false);
        return;
      }
      if (ref.inForce && (!ref.forceEmail.trim() || !ref.forcePassword)) {
        displayWarning("Informe e-mail e senha para continuar no modo Force.");
        setGoogleLoading(false);
        return;
      }
      const dataLoginProps: SignInProps = {
        slug: currentSlug.trim(),
        ambient: ref.ambient,
        inForce: ref.inForce,
        email: ref.inForce ? ref.forceEmail.trim() : undefined,
        password: ref.inForce ? ref.forcePassword : undefined,
        googleToken: token.trim(),
      };
      try {
        await signIn(dataLoginProps);
        setGoogleToken(null);
        localStorage.removeItem("googleToken");
      } catch (err: any) {
        const msg =
          err?.response?.data?.error || err?.message || "Erro ao fazer login.";
        displayError(msg);
        setGoogleToken(null);
        localStorage.removeItem("googleToken");
      } finally {
        loginValuesRef.current.forcePassword = "";
        setForcePassword("");
        setGoogleLoading(false);
      }
    },
    [signIn],
  );

  useEffect(() => {
    const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;
    if (!clientId?.trim()) return;

    const script = document.createElement("script");
    script.src = "https://accounts.google.com/gsi/client";
    script.async = true;
    script.defer = true;
    script.onerror = () => {
      displayWarning(
        "Erro ao carregar serviços do Google. Verifique sua conexão.",
      );
      setShowGoogleButton(true);
    };
    script.onload = () => {
      const w = window as unknown as { google?: { accounts?: { id?: any } } };
      if (!w.google?.accounts?.id) {
        setShowGoogleButton(true);
        return;
      }
      try {
        w.google.accounts.id.initialize({
          client_id: clientId.trim(),
          auto_select: false,
          callback: (response: { credential?: string }) => {
            if (!response?.credential?.trim()) {
              displayWarning("Não foi possível obter o token do Google.");
              setGoogleLoading(false);
              return;
            }
            const token = response.credential.trim();
            setGoogleToken(token);
            performLoginWithGoogle(token);
          },
        });
        showGooglePromptRef.current = () => {
          try {
            w.google!.accounts!.id!.prompt(() => { });
          } catch {
            setShowGoogleButton(true);
          }
          setGoogleLoading(false);
        };
        const container = document.getElementById("googleSignInBtn");
        if (container && w.google?.accounts?.id) {
          w.google.accounts.id.renderButton(container, {
            type: "standard",
            theme: "filled_blue",
            size: "large",
            text: "signin_with",
            shape: "pill",
          });
        }
        setShowGoogleButton(true);
      } catch (e) {
        displayWarning("Erro ao inicializar login do Google.");
        setShowGoogleButton(true);
      }
    };
    document.body.appendChild(script);
    return () => {
      if (document.body.contains(script)) document.body.removeChild(script);
    };
  }, [performLoginWithGoogle]);

  const onLoginWithGoogleClick = () => {
    const currentSlug =
      selectedOption?.value || slug || localStorage.getItem("slug") || "";
    if (!currentSlug?.trim()) {
      displayWarning("Selecione o cliente antes de continuar.");
      return;
    }
    if (inForce && (!forceEmail.trim() || !forcePassword)) {
      displayWarning("Informe e-mail e senha para continuar no modo Force.");
      return;
    }
    setGoogleLoading(true);
    const token = googleToken;
    if (token?.trim()) {
      performLoginWithGoogle(token);
      return;
    }
    if (showGooglePromptRef.current) {
      showGooglePromptRef.current();
    } else {
      setShowGoogleButton(true);
      setGoogleLoading(false);
      displayWarning("Carregando login do Google. Use o botão abaixo.");
    }
  };

  const onSubmitForm = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    onLoginWithGoogleClick();
  };

  return (
    <>
      <Helmet>
        <title>Login</title>
      </Helmet>

      <div className="bg-page login-page">
        <div className="login-layout">
          <section className="login-hero">
            <div className="login-hero-brand">
              <img
                src={heroLogo}
                className="login-hero-logo"
                alt="Tech4Humans"
              />
              <span className="login-hero-divider" />
            </div>
            <p className="login-hero-tagline">
              Edite e publique formulários com agilidade.
            </p>
          </section>

          <div className="login-form-panel">
            <form onSubmit={onSubmitForm} className="login-card">
              <img
                src={logo}
                className="login-card-logo"
                alt="Logo Tech4Humans"
              />
              <div className="login-card-header">
                <h2>Bem-vindo ao T4H Forms</h2>
                <span>Faça login na plataforma para continuar</span>
              </div>

              <div className="login-field">
                <label htmlFor="force-mode">Está no Force?</label>
                <select
                  id="force-mode"
                  className="login-input"
                  value={forceMode}
                  onChange={(e) => {
                    const mode = e.target.value === "force" ? "force" : "rc";
                    if (e.target.value === "") {
                      setForceMode("");
                      setInForce(false);
                      setSelectedOption(null);
                      setSlug("");
                      localStorage.setItem("inForce", "false");
                      localStorage.removeItem("slug");
                      return;
                    }
                    setForceMode(mode);
                    const isForce = mode === "force";
                    setInForce(isForce);
                    setSelectedOption(null);
                    setSlug("");
                    setClientListAmbient("");
                    localStorage.setItem("inForce", String(isForce));
                    localStorage.removeItem("slug");
                  }}
                >
                  <option value="">Escolha uma opção</option>
                  <option value="rc">Não</option>
                  <option value="force">Sim</option>
                </select>
              </div>

              {forceMode !== "" && (
                <div className="login-field">
                  <label htmlFor="login-ambient">Ambiente</label>
                  <select
                    id="login-ambient"
                    className="login-input"
                    value={clientListAmbient}
                    title="Filtra a lista de clientes e define o ambiente (hml/prod) enviado no login."
                    onChange={(e) => {
                      const next =
                        e.target.value === "hml"
                          ? "hml"
                          : e.target.value === "prod"
                            ? "prod"
                            : "";
                      setClientListAmbient(next as ClientAmbient | "");
                      setSelectedOption(null);
                      setSlug("");
                      localStorage.removeItem("slug");
                    }}
                  >
                    <option value="">Escolha o ambiente</option>
                    <option value="prod">Produção</option>
                    <option value="hml">Homologação</option>
                  </select>
                </div>
              )}

              {forceMode !== "" && clientListAmbient !== "" && (
                <div className="login-field">
                  <label htmlFor="cliente-select-search">Cliente</label>
                  <Select<ClientOption>
                    options={filteredClientOptions}
                    value={selectedOption}
                    isLoading={clientsLoading}
                    isDisabled={clientsLoading}
                    onChange={(newValue) => {
                      if (newValue && "value" in newValue && newValue.value) {
                        const slugValue = String(newValue.value).trim();
                        setSlug(slugValue);
                        localStorage.setItem("slug", slugValue);
                        setSelectedOption(newValue);
                      } else {
                        setSlug("");
                        localStorage.setItem("slug", "");
                        setSelectedOption(null);
                      }
                    }}
                    placeholder="Escolha uma opção"
                    inputId="cliente-select-search"
                    className="w-full bg-text-field-select"
                    classNamePrefix="react-select"
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    styles={{
                      container: (base) => ({
                        ...base,
                        width: "100%",
                      }),
                      control: (base, state) => ({
                        ...base,
                        minHeight: "36px",
                        fontSize: "14px",
                        borderRadius: "8px",
                        borderColor: state.isFocused ? "#8B5CF6" : "#E5E7EB",
                        backgroundColor: "#F9FAFB",
                        boxShadow: state.isFocused
                          ? "0 0 0 3px rgba(139, 92, 246, 0.12)"
                          : "none",
                      }),
                      valueContainer: (base) => ({
                        ...base,
                        padding: "2px 12px",
                      }),
                      placeholder: (base) => ({
                        ...base,
                        color: "#9CA3AF",
                      }),
                      menuPortal: (base) => ({
                        ...base,
                        zIndex: 9999,
                      }),
                    }}
                  />
                </div>
              )}

              {inForce && (
                <>
                  <div className="login-field">
                    <label htmlFor="force-email">
                      E-mail do force<span className="login-required">*</span>
                    </label>
                    <input
                      id="force-email"
                      type="email"
                      autoComplete="email"
                      required
                      value={forceEmail}
                      onChange={(e) => setForceEmail(e.target.value)}
                      className="login-input"
                      placeholder="Digite seu e-mail"
                    />
                  </div>

                  <div className="login-field">
                    <label htmlFor="force-password">
                      Senha do force<span className="login-required">*</span>
                    </label>
                    <input
                      id="force-password"
                      type="password"
                      autoComplete="current-password"
                      required
                      value={forcePassword}
                      onChange={(e) => setForcePassword(e.target.value)}
                      className="login-input"
                      placeholder="Digite sua senha"
                    />
                  </div>
                </>
              )}

              <div className="login-field login-google-row">
                {!showGoogleButton && (loading || googleLoading) && (
                  <span className="login-google-loading">
                    Carregando login do Google...
                  </span>
                )}
                <div id="googleSignInBtn" className="login-google-container" />
              </div>

              <div className="login-card-footer">
                <span>© Tech4Humans · Todos os direitos reservados</span>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};
export default Login;
